import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Filter, FileText, CheckCircle2, Clock, XCircle, Wrench,
  TrendingUp, ArrowDownLeft, Sparkles, FileDown,
} from 'lucide-react';
import CustomerAccountLayout from '../../components/CustomerAccountLayout';

/* ─────────────────────────────────────────────────────────────────────
 * /account/rewards/statement — Full Rewards Statement (iter86, Jun 2026).
 *
 * Replaces the deprecated /account/notifications page. The customer's
 * single source of truth for *why* their Gift Card Credit balance is
 * what it is: every earning, every pending review, every redeemed
 * dollar, every admin adjustment, sorted by date.
 *
 * Endpoint: GET /api/customers/me/statement?type=all|earned|redeemed|
 * pending&from=YYYY-MM-DD&to=YYYY-MM-DD
 *
 * The page is intentionally scrollable & filterable rather than
 * paginated — most customers have <100 rows for many years, and the
 * "scroll the timeline" UX outperforms numbered pagination for
 * financial transparency.
 * ─────────────────────────────────────────────────────────────────── */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { key: 'all',      label: 'All Activity',     Icon: FileText },
  { key: 'earned',   label: 'Credits Earned',   Icon: TrendingUp },
  { key: 'redeemed', label: 'Credits Redeemed', Icon: ArrowDownLeft },
  { key: 'pending',  label: 'Pending Activity', Icon: Clock },
];

const STATUS_PILLS = {
  Completed:       { tone: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', Icon: CheckCircle2 },
  Pending:         { tone: 'bg-amber-500/15 text-amber-300 border-amber-500/30', Icon: Clock },
  'Pending Review':{ tone: 'bg-amber-500/15 text-amber-300 border-amber-500/30', Icon: Clock },
  Approved:        { tone: 'bg-sky-500/15 text-sky-300 border-sky-500/30', Icon: CheckCircle2 },
  Delivered:       { tone: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', Icon: CheckCircle2 },
  Rejected:        { tone: 'bg-rose-500/15 text-rose-300 border-rose-500/30', Icon: XCircle },
  Adjustment:      { tone: 'bg-violet-500/15 text-violet-300 border-violet-500/30', Icon: Wrench },
};

const money = (cents) => {
  const v = Math.abs((cents || 0) / 100);
  const sign = cents < 0 ? '-' : '';
  return `${sign}$${v.toFixed(2)}`;
};
const moneySigned = (cents) => {
  const v = Math.abs((cents || 0) / 100);
  const sign = cents > 0 ? '+' : cents < 0 ? '-' : '';
  return `${sign}$${v.toFixed(2)}`;
};
const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const AccountRewardsStatement = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('nofee_customer_token') : null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      const qs = [`type=${encodeURIComponent(filter)}`];
      if (fromDate) qs.push(`from_=${encodeURIComponent(fromDate)}`);
      if (toDate)   qs.push(`to=${encodeURIComponent(toDate)}`);
      try {
        const r = await axios.get(`${API}/customers/me/statement?${qs.join('&')}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setData(r.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [token, filter, fromDate, toDate]);

  const s = data?.summary || {};
  const rows = data?.rows || [];
  const totalCount = data?.total_count || 0;

  // CSV export — pure client-side, no backend round-trip needed.
  // Future PDF/server-side export will plug into the same shape.
  const exportCsv = useMemo(() => () => {
    const header = ['Date', 'Activity', 'Points', 'Amount (USD)', 'Status'];
    const lines = [header.join(',')];
    for (const r of rows) {
      const date = (r.created_at || '').slice(0, 10);
      const activity = `"${(r.label || '').replace(/"/g, '""')}"`;
      const pts = r.points ?? 0;
      const amt = ((r.cents || 0) / 100).toFixed(2);
      const status = `"${(r.status || '').replace(/"/g, '""')}"`;
      lines.push([date, activity, pts, amt, status].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nofee-rewards-statement-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [rows]);

  return (
    <CustomerAccountLayout
      currentKey="statement"
      title="Rewards Statement"
      subtitle="Complete history of all Gift Card Credits earned, redeemed, pending, and adjusted."
    >
      <Link
        to="/account/rewards"
        data-testid="statement-back-btn"
        className="inline-flex items-center gap-1.5 text-[12.5px] text-yellow-300/85 hover:text-yellow-300 font-bold mb-4"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to My Rewards
      </Link>

      {/* ─── Summary tiles ─── */}
      <div
        data-testid="statement-summary-tiles"
        className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-5"
      >
        <div className="p-3.5 rounded-2xl bg-yellow-500/[0.08] border border-yellow-500/30">
          <div className="text-[10px] uppercase tracking-[0.18em] text-yellow-300/85 font-bold">Available</div>
          <div className="mt-1 text-2xl sm:text-[28px] font-extrabold text-yellow-300 tabular-nums" data-testid="statement-tile-available">
            {money(s.gift_credit_cents)}
          </div>
        </div>
        <div className="p-3.5 rounded-2xl bg-amber-500/[0.06] border border-amber-500/25">
          <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300/85 font-bold">Pending Review</div>
          <div className="mt-1 text-2xl sm:text-[28px] font-extrabold text-amber-300 tabular-nums" data-testid="statement-tile-pending">
            {money(s.pending_redemption_cents)}
          </div>
        </div>
        <div className="p-3.5 rounded-2xl bg-emerald-500/[0.06] border border-emerald-500/25">
          <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300/85 font-bold">Redeemed</div>
          <div className="mt-1 text-2xl sm:text-[28px] font-extrabold text-emerald-300 tabular-nums" data-testid="statement-tile-redeemed">
            {money(s.redeemed_lifetime_cents)}
          </div>
        </div>
        <div className="p-3.5 rounded-2xl bg-white/[0.04] border border-white/10">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold">Lifetime Earned</div>
          <div className="mt-1 text-2xl sm:text-[28px] font-extrabold text-white tabular-nums" data-testid="statement-tile-lifetime">
            {money(s.lifetime_earned_cents)}
          </div>
        </div>
      </div>

      {/* ─── Filters ─── */}
      <div data-testid="statement-filters" className="space-y-3 mb-4">
        <div className="flex items-center gap-1.5 flex-wrap" role="tablist">
          {FILTERS.map(({ key, label, Icon }) => {
            const active = filter === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                role="tab"
                aria-selected={active}
                data-testid={`statement-filter-${key}`}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-[12.5px] font-bold transition-colors ${
                  active
                    ? 'border-yellow-400 bg-yellow-500/[0.15] text-yellow-200'
                    : 'border-white/12 bg-white/[0.02] text-white/65 hover:border-white/25 hover:text-white'
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-[11.5px] text-white/55">
            <Filter className="w-3.5 h-3.5" /> Date range:
          </div>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            data-testid="statement-date-from"
            className="px-2.5 py-1.5 rounded-lg bg-black/40 border border-white/10 text-white text-[12.5px] focus:border-yellow-500/50 outline-none"
            aria-label="From date"
          />
          <span className="text-white/35 text-[12px]">→</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            data-testid="statement-date-to"
            className="px-2.5 py-1.5 rounded-lg bg-black/40 border border-white/10 text-white text-[12.5px] focus:border-yellow-500/50 outline-none"
            aria-label="To date"
          />
          {(fromDate || toDate) && (
            <button
              type="button"
              onClick={() => { setFromDate(''); setToDate(''); }}
              data-testid="statement-date-clear"
              className="text-[11.5px] text-white/55 hover:text-white underline"
            >
              clear
            </button>
          )}
          <div className="flex-1" />
          <button
            type="button"
            onClick={exportCsv}
            disabled={rows.length === 0}
            data-testid="statement-export-csv"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/12 hover:border-yellow-500/40 hover:bg-yellow-500/[0.06] text-white/75 hover:text-yellow-300 text-[12px] font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FileDown className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      {/* ─── Statement table ─── */}
      {loading ? (
        <div className="text-white/45 text-[14px]" data-testid="statement-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div
          data-testid="statement-empty"
          className="p-8 sm:p-10 rounded-3xl border border-white/10 bg-white/[0.02] text-center"
        >
          <Sparkles className="w-7 h-7 text-yellow-300/70 mx-auto mb-3" />
          <h3 className="text-[15px] font-bold text-white tracking-tight">No activity in this filter</h3>
          <p className="mt-2 text-[12.5px] text-white/55 max-w-md mx-auto">
            Try widening your date range or switching the filter to <strong className="text-yellow-300/85">All Activity</strong>.
          </p>
        </div>
      ) : (
        <div
          data-testid="statement-table-wrapper"
          className="rounded-2xl border border-white/8 bg-white/[0.015] overflow-hidden"
        >
          {/* Desktop table */}
          <table className="hidden sm:table w-full" data-testid="statement-table">
            <thead>
              <tr className="text-left text-[10.5px] uppercase tracking-[0.18em] text-white/55 font-bold border-b border-white/8 bg-black/30">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Activity</th>
                <th className="px-4 py-3 text-right">Points</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const pill = STATUS_PILLS[r.status] || STATUS_PILLS.Completed;
                return (
                  <tr
                    key={r.id}
                    data-testid={`statement-row-${r.id}`}
                    data-status={r.status}
                    className="border-b border-white/[0.05] last:border-b-0 hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="px-4 py-3 text-[12.5px] text-white/75 whitespace-nowrap">
                      {formatDate(r.created_at)}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-white">{r.label}</td>
                    <td className="px-4 py-3 text-right text-[12.5px] text-white/85 tabular-nums">
                      {r.points > 0 ? `+${r.points}` : r.points || '—'}
                    </td>
                    <td className={`px-4 py-3 text-right text-[13px] font-extrabold tabular-nums ${
                      r.cents > 0 ? 'text-yellow-300' : r.cents < 0 ? 'text-rose-300' : 'text-white/45'
                    }`}>
                      {moneySigned(r.cents)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10.5px] font-bold uppercase tracking-wider ${pill.tone}`}>
                        <pill.Icon className="w-3 h-3" /> {r.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Mobile cards */}
          <ul className="sm:hidden divide-y divide-white/[0.05]" data-testid="statement-cards">
            {rows.map((r) => {
              const pill = STATUS_PILLS[r.status] || STATUS_PILLS.Completed;
              return (
                <li key={r.id} className="p-3.5" data-testid={`statement-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[13px] text-white font-semibold">{r.label}</div>
                      <div className="text-[11px] text-white/45 mt-0.5">{formatDate(r.created_at)}</div>
                    </div>
                    <div className={`text-[13.5px] font-extrabold tabular-nums shrink-0 ${
                      r.cents > 0 ? 'text-yellow-300' : r.cents < 0 ? 'text-rose-300' : 'text-white/45'
                    }`}>
                      {moneySigned(r.cents)}
                    </div>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between">
                    <span className="text-[11px] text-white/55 tabular-nums">
                      {r.points > 0 ? `+${r.points} pts` : r.points ? `${r.points} pts` : '—'}
                    </span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10.5px] font-bold uppercase tracking-wider ${pill.tone}`}>
                      <pill.Icon className="w-3 h-3" /> {r.status}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="px-4 py-2.5 border-t border-white/8 bg-black/20 text-[11.5px] text-white/45 flex items-center justify-between">
            <span data-testid="statement-row-count">
              Showing {rows.length} of {totalCount} {totalCount === 1 ? 'entry' : 'entries'}
            </span>
            <span>{s.conversion}</span>
          </div>
        </div>
      )}
    </CustomerAccountLayout>
  );
};

export default AccountRewardsStatement;
